
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SwappableCard } from "../SwappableCard";
import { Users, Check, X, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { startOfMonth, endOfMonth, format } from "date-fns";
import { ResizableSwappableCard } from "./ResizableSwappableCard";

interface InvitationsStatsCardProps {
  rearrangingEnabled: boolean;
  onRemove: (id: string) => void;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
}

export function InvitationsStatsCard({ 
  rearrangingEnabled, 
  onRemove,
  size = 'medium',
  onResize
}: InvitationsStatsCardProps) {
  // Get current month's start and end dates
  const currentMonth = new Date();
  const startOfCurrentMonth = startOfMonth(currentMonth);
  const endOfCurrentMonth = endOfMonth(currentMonth);
  
  const { data: invitationsStats, isLoading } = useQuery({
    queryKey: ["invitationsStats", format(startOfCurrentMonth, "yyyy-MM")],
    queryFn: async () => {
      // Get all invitations for the current month
      const { data: invitations, error } = await supabase
        .from("user_invitations")
        .select("*")
        .gte("created_at", startOfCurrentMonth.toISOString())
        .lte("created_at", endOfCurrentMonth.toISOString());
      
      if (error) {
        console.error("Error fetching invitation stats:", error);
        throw error;
      }

      // Calculate statistics
      const total = invitations.length;
      const accepted = invitations.filter(inv => inv.status === "accepted").length;
      const expired = invitations.filter(inv => inv.status === "expired").length;
      const pending = invitations.filter(inv => inv.status === "pending").length;
      
      // Calculate conversion rate (accepted / total)
      const conversionRate = total > 0 ? Math.round((accepted / total) * 100) : 0;
      
      return {
        total,
        accepted,
        expired,
        pending,
        conversionRate
      };
    }
  });

  const cardContent = (
    <>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-medium flex items-center">
          <Users className="h-5 w-5 mr-2 text-blue-500" />
          User Invitations
          <span className="ml-2 text-xs text-muted-foreground font-normal flex items-center">
            <Calendar className="h-3 w-3 mr-1" />
            {format(currentMonth, "MMMM yyyy")}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center items-center h-24">
            <p className="text-muted-foreground">Loading stats...</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-muted/50 p-3 rounded-md text-center">
                <p className="text-2xl font-semibold">{invitationsStats?.total || 0}</p>
                <p className="text-xs text-muted-foreground">Total Invited</p>
              </div>
              <div className="bg-green-100 p-3 rounded-md text-center">
                <p className="text-2xl font-semibold text-green-700 flex items-center justify-center">
                  <Check className="h-4 w-4 mr-1" />
                  {invitationsStats?.accepted || 0}
                </p>
                <p className="text-xs text-green-700/70">Accepted</p>
              </div>
              <div className="bg-red-100 p-3 rounded-md text-center">
                <p className="text-2xl font-semibold text-red-700 flex items-center justify-center">
                  <X className="h-4 w-4 mr-1" />
                  {invitationsStats?.expired || 0}
                </p>
                <p className="text-xs text-red-700/70">Expired</p>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <div className="text-sm">
                <span className="text-muted-foreground">Pending: </span>
                <span className="font-medium">{invitationsStats?.pending || 0}</span>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">Conversion Rate: </span>
                <span className="font-medium">{invitationsStats?.conversionRate || 0}%</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </>
  );

  if (rearrangingEnabled && onResize) {
    return (
      <ResizableSwappableCard 
        id="invitations-stats" 
        size={size}
        showHandle={rearrangingEnabled} 
        showResizeControl={rearrangingEnabled}
        onRemove={onRemove}
        onResize={(newSize) => onResize("invitations-stats", newSize)}
      >
        {cardContent}
      </ResizableSwappableCard>
    );
  }

  return (
    <SwappableCard 
      id="invitations-stats" 
      showHandle={rearrangingEnabled} 
      onRemove={rearrangingEnabled ? onRemove : undefined}
    >
      {cardContent}
    </SwappableCard>
  );
}
